import React, { useMemo, useState } from 'react';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import Co2OutlinedIcon from '@mui/icons-material/Co2Outlined';
import RecyclingOutlinedIcon from '@mui/icons-material/RecyclingOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  CARBON_AVOIDANCE_REPORT_METRIC_GLOSSARY_SECTIONS,
  GENERAL_ESG_REPORT_METRIC_GLOSSARY_SECTIONS,
  WASTE_CIRCULARITY_REPORT_METRIC_GLOSSARY_SECTIONS,
  type GlossarySection,
} from '../../constants/reportMetricGlossary';
import {
  localizeGlossarySections,
  localizedGlossarySectionTitle,
} from '../../helpers/report-metric-glossary.helper';
import { SetupCoachmark } from '../selfSetup/SetupCoachmark';

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function HighlightText({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === q.toLowerCase() ? (
          <Box
            key={i}
            component="mark"
            sx={{
              bgcolor: 'warning.light',
              color: 'text.primary',
              px: 0.25,
              borderRadius: 0.5,
            }}
          >
            {part}
          </Box>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

function filterSections(sections: GlossarySection[], query: string): GlossarySection[] {
  const q = query.trim().toLowerCase();
  if (!q) return sections;
  return sections
    .map((section) => ({
      ...section,
      rows: section.rows.filter(
        (row) =>
          row.metric.toLowerCase().includes(q) || row.meaning.toLowerCase().includes(q)
      ),
    }))
    .filter((section) => section.rows.length > 0);
}

function countRows(sections: GlossarySection[]): number {
  return sections.reduce((acc, s) => acc + s.rows.length, 0);
}

function DefinitionsReportCard({
  title,
  subtitle,
  icon,
  sections,
  query,
  totalDefinitions,
  filteredCount,
  highlightHeader = false,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  sections: GlossarySection[];
  query: string;
  totalDefinitions: number;
  filteredCount: number;
  /** Mark header for multi-hole coachmark spotlight. */
  highlightHeader?: boolean;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const trimmedQuery = query.trim();
  const showEmpty = sections.length === 0;

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: '16px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        {...(highlightHeader ? { 'data-setup-hole': true } : {})}
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box sx={{ color: 'primary.main', display: 'flex', '& svg': { fontSize: 44 } }}>{icon}</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              <Chip
                size="small"
                variant="outlined"
                color="primary"
                label={
                  trimmedQuery
                    ? t('esgReports.definitions.definitionsCountWithTotal', {
                        filtered: filteredCount,
                        total: totalDefinitions,
                      })
                    : t('esgReports.definitions.definitionsCount', { count: totalDefinitions })
                }
              />
            </Stack>
          </Box>
        </Stack>
      </Box>

      <CardContent sx={{ flex: 1, pt: 2.5, '&:last-child': { pb: 2.5 } }}>
        {showEmpty ? (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              borderRadius: '12px',
              borderStyle: 'dashed',
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('esgReports.definitions.noResults')}
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {sections.map((section) => (
              <Paper
                key={section.sectionKey}
                variant="outlined"
                sx={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    bgcolor: 'action.hover',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {localizedGlossarySectionTitle(section.sectionKey, t)}
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: { xs: '40%', sm: '34%' } }}>
                          {t('esgReports.definitions.metricColumn')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t('esgReports.definitions.meaningColumn')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {section.rows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ verticalAlign: 'top', fontWeight: 500, pr: 1 }}>
                            <HighlightText text={row.metric} query={query} />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top', color: 'text.secondary' }}>
                            <HighlightText text={row.meaning} query={query} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export function ReportMetricDefinitionsTab({
  tourTip = null,
  onTourNext,
  onTourDismiss,
  tourNextLabel,
  tourSearchTitle,
  tourSearchSubtitle,
  tourCardsTitle,
  tourCardsSubtitle,
}: {
  tourTip?: 'metricSearch' | 'metricCards' | null;
  onTourNext?: () => void;
  onTourDismiss?: () => void;
  tourNextLabel?: string;
  tourSearchTitle?: string;
  tourSearchSubtitle?: string;
  tourCardsTitle?: string;
  tourCardsSubtitle?: string;
} = {}) {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');

  const generalSections = useMemo(
    () => localizeGlossarySections(GENERAL_ESG_REPORT_METRIC_GLOSSARY_SECTIONS, t),
    [t, i18n.language],
  );
  const wasteSections = useMemo(
    () => localizeGlossarySections(WASTE_CIRCULARITY_REPORT_METRIC_GLOSSARY_SECTIONS, t),
    [t, i18n.language],
  );
  const carbonSections = useMemo(
    () => localizeGlossarySections(CARBON_AVOIDANCE_REPORT_METRIC_GLOSSARY_SECTIONS, t),
    [t, i18n.language],
  );

  const filteredGeneral = useMemo(
    () => filterSections(generalSections, query),
    [generalSections, query],
  );
  const filteredWaste = useMemo(
    () => filterSections(wasteSections, query),
    [wasteSections, query],
  );
  const filteredCarbon = useMemo(
    () => filterSections(carbonSections, query),
    [carbonSections, query],
  );

  const generalTotal = useMemo(() => countRows(generalSections), [generalSections]);
  const wasteTotal = useMemo(() => countRows(wasteSections), [wasteSections]);
  const carbonTotal = useMemo(() => countRows(carbonSections), [carbonSections]);

  const generalShown = countRows(filteredGeneral);
  const wasteShown = countRows(filteredWaste);
  const carbonShown = countRows(filteredCarbon);

  return (
    <Box sx={{ mt: 2 }}>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('esgReports.definitions.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('esgReports.definitions.subtitle')}
        </Typography>
        <SetupCoachmark
          open={tourTip === 'metricSearch'}
          title={tourSearchTitle ?? t('selfSetup.tour.esgReportsMetricSearchTitle')}
          subtitle={
            tourSearchSubtitle ?? t('selfSetup.tour.esgReportsMetricSearchSubtitle')
          }
          onDismiss={() => onTourDismiss?.()}
          onNext={() => onTourNext?.()}
          nextLabel={tourNextLabel ?? t('selfSetup.tourNext')}
        >
          <Box id="self-setup-esg-reports-metric-search">
            <TextField
              fullWidth
              size="small"
              placeholder={t('esgReports.definitions.searchPlaceholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              inputProps={{ 'aria-label': t('esgReports.definitions.searchAria') }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: { sm: 480 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: theme.palette.background.default,
                },
              }}
            />
          </Box>
        </SetupCoachmark>
      </Stack>

      <SetupCoachmark
        open={tourTip === 'metricCards'}
        compact
        placement="left"
        title={tourCardsTitle ?? t('selfSetup.tour.esgReportsMetricCardsTitle')}
        subtitle={
          tourCardsSubtitle ?? t('selfSetup.tour.esgReportsMetricCardsSubtitle')
        }
        onDismiss={() => onTourDismiss?.()}
        onNext={() => onTourNext?.()}
        nextLabel={tourNextLabel ?? t('selfSetup.tourDone')}
        fullWidth
      >
        <Grid
          container
          spacing={3}
          id="self-setup-esg-reports-metric-cards"
        >
          <Grid item xs={12} lg={6}>
            <DefinitionsReportCard
              title={t('esgReports.cards.general.title')}
              subtitle={t('esgReports.definitions.generalSubtitle')}
              icon={<AssessmentOutlinedIcon />}
              sections={filteredGeneral}
              query={query}
              totalDefinitions={generalTotal}
              filteredCount={generalShown}
              highlightHeader={tourTip === 'metricCards'}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <DefinitionsReportCard
              title={t('esgReports.cards.gri306.title')}
              subtitle={t('esgReports.definitions.gri306Subtitle')}
              icon={<RecyclingOutlinedIcon />}
              sections={filteredWaste}
              query={query}
              totalDefinitions={wasteTotal}
              filteredCount={wasteShown}
              highlightHeader={tourTip === 'metricCards'}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <DefinitionsReportCard
              title={t('esgReports.cards.carbon.title')}
              subtitle={t('esgReports.definitions.carbonSubtitle')}
              icon={<Co2OutlinedIcon />}
              sections={filteredCarbon}
              query={query}
              totalDefinitions={carbonTotal}
              filteredCount={carbonShown}
            />
          </Grid>
        </Grid>
      </SetupCoachmark>
    </Box>
  );
}
